import {
  BlockStatus,
  isDraftExpired,
  isValidDate,
  normalizeBlockTitle,
  spansOverlap,
  type LiveTimeBlock,
  type Span,
  type TimeBlockData,
  type TimeBlockRecord,
  RecordType,
} from '@wiztime/core';
import { ValidationError } from '../errors';
import { assertWithinDay } from './placement';
import type { WriteSession } from './session';

export interface CreateBlockOptions {
  readonly note?: string | null;
  /** Block title (2026-10b §5.1); normalized on write, empty becomes null. */
  readonly title?: string | null;
}

/** One block's new span inside `updateBlockSpans`. */
export interface SpanChange {
  readonly id: string;
  readonly startMin: number;
  readonly endMin: number;
}

export interface BlockPatch {
  readonly startMin?: number;
  readonly endMin?: number;
  readonly projectId?: string;
  /** `null` clears the note; `undefined` keeps it. */
  readonly note?: string | null;
  /** `null` clears the title; `undefined` keeps it. Normalized on write. */
  readonly title?: string | null;
}

/**
 * What the picker's single commit command `editBlocks` may write (2026-10b §2.5): only the
 * fields the user actually changed are present. `span` is only valid for a single block —
 * the start and end of a merged group are read-only in the picker.
 */
export interface EditBlocksPatch {
  readonly projectId?: string;
  /** `null` clears the title. */
  readonly title?: string | null;
  /** `null` clears the note. */
  readonly note?: string | null;
  readonly span?: Span;
}

/**
 * What the draft-confirming commands report (2026-10b §10.1): the blocks that are confirmed
 * now, and the drafts removed instead because confirmed blocks of the same project already
 * cover their whole span (the exact-duplicate guard, written as a tombstone like a cleared
 * draft). A confirmed block left as it was is listed in `confirmed` too — it is confirmed.
 * For `setGroupProject`, `confirmed` holds every record the command wrote.
 */
export interface ConfirmResult {
  readonly confirmed: TimeBlockRecord[];
  /** Ids of the drafts removed as duplicates; a single undo step restores them. */
  readonly removedAsDuplicate: string[];
}

function confirmed(data: TimeBlockData): TimeBlockData {
  return { ...data, status: BlockStatus.Confirmed, draftSource: null };
}

/** Live blocks of `date` that have the given status. */
async function liveBlocksOf(
  session: WriteSession,
  date: string,
  status: BlockStatus,
): Promise<LiveTimeBlock[]> {
  const blocks = await session.tx.listByTypeAndDate(RecordType.TimeBlock, date);
  return blocks.filter(
    (block): block is LiveTimeBlock => block.data !== null && block.data.status === status,
  );
}

/** Whether the union of `others` covers every minute of `span`. */
function unionCovers(span: Span, others: readonly Span[]): boolean {
  let covered = span.startMin;
  for (const other of [...others].sort((a, b) => a.startMin - b.startMin)) {
    if (other.startMin > covered) return false;
    covered = Math.max(covered, other.endMin);
    if (covered >= span.endMin) return true;
  }
  return covered >= span.endMin;
}

/**
 * Spans of the live confirmed blocks of `date` per project, as of now: what the duplicate guard
 * of the draft-confirming commands compares a draft with. Taken once per command, so the result
 * of a batch does not depend on the order its drafts are processed in.
 */
async function confirmedSpansByProject(session: WriteSession, date: string): Promise<Map<string, Span[]>> {
  const byProject = new Map<string, Span[]>();
  for (const block of await liveBlocksOf(session, date, BlockStatus.Confirmed)) {
    const spans = byProject.get(block.data.projectId) ?? [];
    spans.push({ startMin: block.data.startMin, endMin: block.data.endMin });
    byProject.set(block.data.projectId, spans);
  }
  return byProject;
}

/** Whether confirmed blocks of the draft's own project already cover its whole span (§10.1). */
function coveredByConfirmed(draft: TimeBlockData, confirmedSpans: ReadonlyMap<string, readonly Span[]>): boolean {
  return unionCovers(draft, confirmedSpans.get(draft.projectId) ?? []);
}

/**
 * Create a confirmed block on `date` with its `countRank` set to the creation time, so its
 * overlapping minutes count towards the newest block (ui-redesign §2.1.5). Returns null without
 * creating anything when existing confirmed blocks of the same project already cover the whole
 * span (the exact-duplicate guard, ui-redesign §2.1.4); the caller tells the user via a toast.
 */
export async function createBlock(
  session: WriteSession,
  date: string,
  span: Span,
  projectId: string,
  options: CreateBlockOptions = {},
): Promise<TimeBlockRecord | null> {
  assertWithinDay(span, await session.settings());
  await session.requireProject(projectId);
  const sameStatus = await liveBlocksOf(session, date, BlockStatus.Confirmed);
  const sameProject = sameStatus.filter((block) => block.data.projectId === projectId);
  if (
    unionCovers(
      span,
      sameProject.map((block) => block.data),
    )
  )
    return null;
  return session.write(RecordType.TimeBlock, session.newId(), {
    personalDate: date,
    startMin: span.startMin,
    endMin: span.endMin,
    tz: session.tz,
    projectId,
    status: BlockStatus.Confirmed,
    note: options.note ?? null,
    title: normalizeBlockTitle(options.title),
    draftSource: null,
    countRank: session.nowMs,
  });
}

/**
 * Change a block's span, project or note. Any edit of a draft also confirms it. A new span must
 * stay within the personal day; overlap with other blocks is allowed. `countRank` is untouched:
 * moving or resizing a block does not move the count attribution of its overlaps.
 */
export async function updateBlock(
  session: WriteSession,
  id: string,
  patch: BlockPatch,
): Promise<TimeBlockRecord> {
  const block = await session.require(RecordType.TimeBlock, id);
  const old = block.data;
  const next: TimeBlockData = {
    ...old,
    startMin: patch.startMin ?? old.startMin,
    endMin: patch.endMin ?? old.endMin,
    projectId: patch.projectId ?? old.projectId,
    note: patch.note === undefined ? old.note : patch.note,
    title: patch.title === undefined ? (old.title ?? null) : normalizeBlockTitle(patch.title),
  };
  const wasDraft = old.status === BlockStatus.Draft;
  const spanChanged = next.startMin !== old.startMin || next.endMin !== old.endMin;
  const unchanged =
    !spanChanged &&
    next.projectId === old.projectId &&
    next.note === old.note &&
    next.title === (old.title ?? null);
  if (unchanged && !wasDraft) return block;

  if (spanChanged) assertWithinDay(next, await session.settings());
  if (next.projectId !== old.projectId) await session.requireProject(next.projectId);
  return session.write(RecordType.TimeBlock, id, wasDraft ? confirmed(next) : next);
}

/**
 * Change the spans of several blocks of one personal day and delete others, as one command and
 * one undo step (a merged group moved or resized as a whole, ui-redesign §8.1). Every new span
 * must lie within the personal day and last longer than zero minutes; `deleteIds` must name
 * blocks of the same day. Drafts among the changed blocks become confirmed; `countRank` is
 * untouched. Returns the updated blocks.
 */
export async function updateBlockSpans(
  session: WriteSession,
  changes: readonly SpanChange[],
  deleteIds: readonly string[] = [],
): Promise<TimeBlockRecord[]> {
  const changeIds = new Set(changes.map((change) => change.id));
  if (changeIds.size !== changes.length) {
    throw new ValidationError('The same block appears twice in the span changes');
  }
  if (deleteIds.some((id) => changeIds.has(id))) {
    throw new ValidationError('A block cannot be both changed and deleted');
  }
  const settings = await session.settings();
  let date: string | undefined;
  const requireSameDate = (personalDate: string): void => {
    date ??= personalDate;
    if (personalDate !== date) {
      throw new ValidationError('All blocks of one span update must belong to the same personal day');
    }
  };

  const written: TimeBlockRecord[] = [];
  for (const change of changes) {
    const block = await session.require(RecordType.TimeBlock, change.id);
    requireSameDate(block.data.personalDate);
    const span = { startMin: change.startMin, endMin: change.endMin };
    const spanChanged = span.startMin !== block.data.startMin || span.endMin !== block.data.endMin;
    // An unchanged confirmed block is not rewritten (as in `updateBlock`): no sync traffic,
    // and the undo step holds only the blocks that actually changed.
    if (!spanChanged && block.data.status !== BlockStatus.Draft) {
      written.push(block);
      continue;
    }
    // Length is only checked as "inside the personal day and longer than zero" (2026-10b
    // §2.5): the minimum duration binds pointer gestures, not stored data — blocks shorter
    // than it arise legally from cutting, sync and settings changes, and must stay movable.
    assertWithinDay(span, settings);
    const next: TimeBlockData = { ...block.data, ...span };
    written.push(
      await session.write(
        RecordType.TimeBlock,
        change.id,
        block.data.status === BlockStatus.Draft ? confirmed(next) : next,
      ),
    );
  }
  for (const id of new Set(deleteIds)) {
    const block = await session.require(RecordType.TimeBlock, id);
    requireSameDate(block.data.personalDate);
    await session.write(RecordType.TimeBlock, id, null);
  }
  return written;
}

/**
 * Move confirmed blocks to another personal date, keeping their identifiers, start and end
 * minutes, time zone and `countRank` (a cross-day drag in the week view, ui-redesign §8.1).
 * Rejects (ValidationError) when any of the blocks is a draft. Returns the moved blocks.
 */
export async function moveBlocksToDate(
  session: WriteSession,
  ids: readonly string[],
  targetDate: string,
): Promise<TimeBlockRecord[]> {
  if (!isValidDate(targetDate)) {
    throw new ValidationError(`Expected a valid YYYY-MM-DD date, got "${targetDate}"`);
  }
  const written: TimeBlockRecord[] = [];
  for (const id of [...new Set(ids)]) {
    const block = await session.require(RecordType.TimeBlock, id);
    if (block.data.status === BlockStatus.Draft) {
      throw new ValidationError('Drafts cannot be moved to another date');
    }
    if (block.data.personalDate === targetDate) continue;
    written.push(await session.write(RecordType.TimeBlock, id, { ...block.data, personalDate: targetDate }));
  }
  return written;
}

/**
 * Give the overlapping minutes of `id` to this block ("count the overlap towards this block",
 * ui-redesign §2.1.5): its `countRank` becomes the maximum over itself and every confirmed
 * block it overlaps, plus one. Nothing else of the record changes, so a later undo restores the
 * previous attribution exactly. Rejects (ValidationError) a draft or a block without overlaps.
 */
export async function setCountRank(session: WriteSession, id: string): Promise<TimeBlockRecord> {
  const block = await session.require(RecordType.TimeBlock, id);
  if (block.data.status === BlockStatus.Draft) {
    throw new ValidationError('A draft has no count attribution to change');
  }
  const confirmedBlocks = await liveBlocksOf(session, block.data.personalDate, BlockStatus.Confirmed);
  const overlapping = confirmedBlocks.filter(
    (other) => other.id !== id && spansOverlap(other.data, block.data),
  );
  if (overlapping.length === 0) {
    throw new ValidationError('The block does not overlap any confirmed block');
  }
  const maxRank = Math.max(...[block, ...overlapping].map((candidate) => candidate.data.countRank ?? 0));
  return session.write(RecordType.TimeBlock, id, { ...block.data, countRank: maxRank + 1 });
}

/**
 * Set the project of every block of a merged group; drafts among them become confirmed. This is
 * also the confirm path of a merged draft group (same project passed): there a draft that
 * confirmed blocks of its project already cover entirely is removed instead of confirmed
 * (`removedAsDuplicate`, 2026-10b §10.1). A draft that changes project is the user's edit and
 * never meets the guard.
 */
export async function setGroupProject(
  session: WriteSession,
  ids: readonly string[],
  projectId: string,
): Promise<ConfirmResult> {
  await session.requireProject(projectId);
  const members = [];
  for (const id of ids) members.push(await session.require(RecordType.TimeBlock, id));
  const dates = new Set(
    members.filter((block) => block.data.status === BlockStatus.Draft).map((block) => block.data.personalDate),
  );
  const spansByDate = new Map<string, Map<string, Span[]>>();
  for (const date of dates) spansByDate.set(date, await confirmedSpansByProject(session, date));

  const result: ConfirmResult = { confirmed: [], removedAsDuplicate: [] };
  for (const block of members) {
    const isDraft = block.data.status === BlockStatus.Draft;
    const projectChanged = block.data.projectId !== projectId;
    if (!projectChanged && !isDraft) continue;
    const spans = spansByDate.get(block.data.personalDate);
    if (isDraft && !projectChanged && spans !== undefined && coveredByConfirmed(block.data, spans)) {
      await session.write(RecordType.TimeBlock, block.id, null);
      result.removedAsDuplicate.push(block.id);
      continue;
    }
    result.confirmed.push(
      await session.write(RecordType.TimeBlock, block.id, confirmed({ ...block.data, projectId })),
    );
  }
  return result;
}

/** Clear (delete) a block or draft. */
export async function clearBlock(session: WriteSession, id: string): Promise<void> {
  await session.require(RecordType.TimeBlock, id);
  await session.write(RecordType.TimeBlock, id, null);
}

/**
 * Clear (delete) several blocks or drafts, e.g. every member of a merged group, as one command.
 * Duplicate ids are cleared once; a missing block fails the whole command. Returns the cleared ids.
 */
export async function clearBlocks(session: WriteSession, ids: readonly string[]): Promise<string[]> {
  const unique = [...new Set(ids)];
  for (const id of unique) {
    await session.require(RecordType.TimeBlock, id);
    await session.write(RecordType.TimeBlock, id, null);
  }
  return unique;
}

/**
 * Confirm one draft, optionally also choosing its project. A confirmed block is left as is.
 * Confirming without a project change runs the exact-duplicate guard (2026-10b §10.1): a draft
 * that confirmed blocks of its project already cover entirely is removed, not confirmed. A
 * draft confirmed together with a different project is the user's edit and skips the guard.
 */
export async function confirmDraft(
  session: WriteSession,
  id: string,
  projectId?: string,
): Promise<ConfirmResult> {
  const block = await session.require(RecordType.TimeBlock, id);
  if (projectId !== undefined) await session.requireProject(projectId);
  const projectChanged = projectId !== undefined && projectId !== block.data.projectId;
  if (block.data.status === BlockStatus.Confirmed && !projectChanged) {
    return { confirmed: [block], removedAsDuplicate: [] };
  }
  if (
    block.data.status === BlockStatus.Draft &&
    !projectChanged &&
    coveredByConfirmed(block.data, await confirmedSpansByProject(session, block.data.personalDate))
  ) {
    await session.write(RecordType.TimeBlock, id, null);
    return { confirmed: [], removedAsDuplicate: [id] };
  }
  const written = await session.write(
    RecordType.TimeBlock,
    id,
    confirmed({ ...block.data, projectId: projectId ?? block.data.projectId }),
  );
  return { confirmed: [written], removedAsDuplicate: [] };
}

async function liveDraftsOf(session: WriteSession, date: string): Promise<LiveTimeBlock[]> {
  return liveBlocksOf(session, date, BlockStatus.Draft);
}

/**
 * Confirm every visible (not expired) draft of `date` in one step. Each draft meets the
 * exact-duplicate guard (2026-10b §10.1), judged against the confirmed blocks as they were
 * when the command started: drafts they cover are removed (`removedAsDuplicate`), the rest
 * confirmed.
 */
export async function confirmAllDrafts(session: WriteSession, date: string): Promise<ConfirmResult> {
  const result: ConfirmResult = { confirmed: [], removedAsDuplicate: [] };
  const settings = await session.settings();
  if (isDraftExpired(date, settings, session.nowMs)) return result;
  const drafts = await liveDraftsOf(session, date);
  if (drafts.length === 0) return result;
  const spans = await confirmedSpansByProject(session, date);
  for (const draft of drafts) {
    if (coveredByConfirmed(draft.data, spans)) {
      await session.write(RecordType.TimeBlock, draft.id, null);
      result.removedAsDuplicate.push(draft.id);
      continue;
    }
    result.confirmed.push(await session.write(RecordType.TimeBlock, draft.id, confirmed(draft.data)));
  }
  return result;
}

/** Clear every draft of `date` in one step (they stay cleared; they are not generated again). */
export async function clearDrafts(session: WriteSession, date: string): Promise<string[]> {
  const ids: string[] = [];
  for (const draft of await liveDraftsOf(session, date)) {
    await session.write(RecordType.TimeBlock, draft.id, null);
    ids.push(draft.id);
  }
  return ids;
}

/**
 * The picker's single commit command (2026-10b §2.5 and §5.4): write the changed fields of a
 * block or of every member of a merged group, as one transaction and one undo step. Only the
 * fields present in `patch` are written; `span` is accepted for a single block only (the
 * start and end of a merged group are read-only in the picker). Any edit of a draft also
 * confirms it; a member that would not change is not rewritten. Returns the records written.
 */
export async function editBlocks(
  session: WriteSession,
  ids: readonly string[],
  patch: EditBlocksPatch,
): Promise<TimeBlockRecord[]> {
  const unique = [...new Set(ids)];
  if (unique.length === 0) throw new ValidationError('editBlocks needs at least one block');
  if (patch.span !== undefined && unique.length > 1) {
    throw new ValidationError('A merged group cannot change its span through editBlocks');
  }
  if (patch.projectId !== undefined) await session.requireProject(patch.projectId);
  const settings = await session.settings();
  const written: TimeBlockRecord[] = [];
  for (const id of unique) {
    const block = await session.require(RecordType.TimeBlock, id);
    const old = block.data;
    const next: TimeBlockData = {
      ...old,
      projectId: patch.projectId ?? old.projectId,
      note: patch.note === undefined ? old.note : patch.note,
      title: patch.title === undefined ? (old.title ?? null) : normalizeBlockTitle(patch.title),
      startMin: patch.span?.startMin ?? old.startMin,
      endMin: patch.span?.endMin ?? old.endMin,
    };
    const wasDraft = old.status === BlockStatus.Draft;
    const spanChanged = next.startMin !== old.startMin || next.endMin !== old.endMin;
    const unchanged =
      !spanChanged &&
      next.projectId === old.projectId &&
      next.note === old.note &&
      next.title === (old.title ?? null);
    if (unchanged && !wasDraft) continue;
    if (spanChanged) assertWithinDay(next, settings);
    written.push(
      await session.write(RecordType.TimeBlock, id, wasDraft ? confirmed(next) : next),
    );
  }
  return written;
}
